import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Award, Printer, Sparkles } from "lucide-react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, errMessage } from "../../lib/api";

// Family-facing speech extras: badge certificates, a friendly progress line,
// and the weekly beta check-in. Neutral, encouraging language throughout.

const CERT_TONE = {
  amber: "#F59E0B", sky: "#0EA5E9", violet: "#8B5CF6",
  rose: "#F43F5E", emerald: "#10B981",
};

const CERT_STYLES = {
  classic: { label: "Classic", emoji: "\u{1F3C6}", accent: null },
  stars: { label: "Stars", emoji: "\u2B50", accent: "#F59E0B" },
  rainbow: { label: "Rainbow", emoji: "\u{1F308}", accent: "#8B5CF6" },
  dino: { label: "Dinosaur", emoji: "\u{1F995}", accent: "#10B981" },
};

const printCertificate = (badge, childName, styleKey = "classic") => {
  const style = CERT_STYLES[styleKey] || CERT_STYLES.classic;
  const color = style.accent || CERT_TONE[badge.color] || CERT_TONE.amber;
  const date = new Date(badge.earned_at || Date.now()).toLocaleDateString();
  const w = window.open("", "_blank", "width=800,height=600");
  if (!w) return toast.error("Please allow pop-ups to print the certificate");
  w.document.write(`<!doctype html><html><head><title>Badge Certificate</title>
    <style>
      body { font-family: Georgia, serif; margin: 0; padding: 40px; text-align: center; }
      .frame { border: 10px double ${color}; border-radius: 24px; padding: 48px 36px; max-width: 640px; margin: 0 auto; }
      h1 { font-size: 34px; color: ${color}; margin: 8px 0; }
      .name { font-size: 30px; margin: 18px 0 6px; }
      .line { font-size: 17px; color: #444; max-width: 460px; margin: 12px auto; }
      .badge { font-size: 22px; font-weight: bold; margin-top: 10px; }
      .date { margin-top: 26px; font-size: 14px; color: #777; }
      .foot { margin-top: 14px; font-size: 12px; color: #999; }
      .deco { font-size: 26px; letter-spacing: 12px; margin-top: 10px; }
      @media print { body { padding: 0; } }
    </style></head><body>
    <div class="frame">
      <div style="font-size:56px">${style.emoji}</div>
      <h1>Certificate of Practice</h1>
      <p class="line">This certificate celebrates</p>
      <p class="name"><b>${childName}</b></p>
      <p class="line">for earning the</p>
      <p class="badge" style="color:${color}">${badge.name} badge</p>
      <p class="line">${badge.line}</p>
      <div class="deco">${style.emoji}${style.emoji}${style.emoji}</div>
      <p class="date">Earned on ${date}</p>
      <p class="foot">M Speech, Language &amp; Math Helper &mdash; celebrating effort and practice</p>
    </div>
    <script>window.onload = () => setTimeout(() => window.print(), 300);</script>
    </body></html>`);
  w.document.close();
};

// ------------------------------------------------------------ parent badges
export const ParentBadges = ({ childId, childName }) => {
  const [badges, setBadges] = useState([]);
  const [picking, setPicking] = useState(null); // badge_key with style menu open

  useEffect(() => {
    if (!childId) return;
    api.get(`/speech/${childId}/badges`)
      .then(({ data }) => setBadges(data.earned)).catch(() => setBadges([]));
  }, [childId]);

  if (!badges.length) return null;
  return (
    <div className="space-y-1.5" data-testid="parent-badges">
      <p className="text-[11px] font-bold flex items-center gap-1">
        <Award className="h-3.5 w-3.5 text-amber-500" aria-hidden="true" /> Earned badges
      </p>
      <div className="flex gap-2 flex-wrap">
        {badges.map((b) => (
          <span key={b.badge_key}
            className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-[11px] font-bold"
            data-testid={`parent-badge-${b.badge_key}`}>
            {b.name}
            {b.kind === "sound" && (
              <button type="button" title="Print a certificate to share"
                onClick={() => setPicking(picking === b.badge_key ? null : b.badge_key)}
                className="p-0.5 rounded hover:bg-muted"
                data-testid={`badge-print-${b.badge_key}`}>
                <Printer className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
              </button>
            )}
          </span>
        ))}
      </div>
      {picking && (
        <div className="flex gap-1.5 flex-wrap items-center" data-testid="certificate-styles">
          <span className="text-[10px] font-bold">Certificate look:</span>
          {Object.entries(CERT_STYLES).map(([key, s]) => (
            <button key={key} type="button"
              onClick={() => {
                printCertificate(badges.find((b) => b.badge_key === picking), childName, key);
                setPicking(null);
              }}
              className="rounded-full border border-border px-2.5 py-0.5 text-[10px] font-bold hover:bg-muted"
              data-testid={`certificate-style-${key}`}>
              {s.emoji} {s.label}
            </button>
          ))}
        </div>
      )}
      <p className="text-[10px] text-muted-foreground">
        Tap the printer on a sound badge, pick a look, and print — lovely for grandparents' fridges.
      </p>
    </div>
  );
};

// --------------------------------------------------------- monthly snapshot
const printSnapshot = (snap) => {
  const w = window.open("", "_blank", "width=800,height=640");
  if (!w) return toast.error("Please allow pop-ups to print the snapshot");
  const badges = snap.badges_earned.length
    ? snap.badges_earned.map((b) => `<span class="chip">${b.icon || "\u2B50"} ${b.name}</span>`).join("")
    : "<span class='chip'>More badges on the way!</span>";
  const sounds = snap.sounds_practiced.length ? snap.sounds_practiced.join(" · ") : "warming up";
  const monthLabel = new Date(`${snap.month}-15T12:00:00Z`)
    .toLocaleDateString(undefined, { month: "long", year: "numeric" });
  w.document.write(`<!doctype html><html><head><title>Practice Snapshot</title>
    <style>
      body { font-family: Georgia, serif; margin: 0; padding: 40px; text-align: center; }
      .frame { border: 10px double #0EA5E9; border-radius: 24px; padding: 42px 36px; max-width: 640px; margin: 0 auto; }
      h1 { font-size: 30px; color: #0EA5E9; margin: 6px 0; }
      .name { font-size: 28px; margin: 12px 0 2px; }
      .stats { display: flex; justify-content: center; gap: 26px; margin: 20px 0; }
      .stat b { display: block; font-size: 30px; color: #0EA5E9; }
      .stat span { font-size: 13px; color: #555; }
      .line { font-size: 15px; color: #444; max-width: 460px; margin: 10px auto; }
      .chip { display: inline-block; border: 1px solid #ddd; border-radius: 999px; padding: 4px 12px; margin: 3px; font-size: 13px; font-weight: bold; }
      .foot { margin-top: 18px; font-size: 11px; color: #999; }
      @media print { body { padding: 0; } }
    </style></head><body>
    <div class="frame">
      <div style="font-size:44px">\u{1F31F}</div>
      <h1>Monthly Practice Snapshot</h1>
      <p class="name"><b>${snap.child_name}</b> &amp; Coach ${snap.coach_name}</p>
      <p class="line">${monthLabel}</p>
      <div class="stats">
        <div class="stat"><b>${snap.practice_days}</b><span>practice day${snap.practice_days === 1 ? "" : "s"}</span></div>
        <div class="stat"><b>${snap.attempts}</b><span>brave tries</span></div>
        <div class="stat"><b>${snap.minutes}</b><span>minutes</span></div>
      </div>
      <p class="line">Sounds explored: <b>${sounds}</b></p>
      <div>${badges}</div>
      <p class="foot">M Speech, Language &amp; Math Helper &mdash; celebrating effort and taking part, never a score.</p>
    </div>
    <script>window.onload = () => setTimeout(() => window.print(), 300);</script>
    </body></html>`);
  w.document.close();
};

export const MonthlySnapshot = ({ childId }) => {
  const [snap, setSnap] = useState(null);

  useEffect(() => {
    if (!childId) return;
    api.get(`/speech/children/${childId}/snapshot`)
      .then(({ data }) => setSnap(data)).catch(() => setSnap(null));
  }, [childId]);

  if (!snap || (!snap.attempts && !snap.practice_days)) return null;
  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50/50 p-3 space-y-1.5"
      data-testid="monthly-snapshot">
      <div className="flex items-center gap-2">
        <Sparkles className="h-3.5 w-3.5 text-sky-500" aria-hidden="true" />
        <p className="text-[11px] font-bold">This month's practice wins</p>
        <button type="button" title="Print or save a shareable snapshot"
          onClick={() => printSnapshot(snap)}
          className="ml-auto p-1 rounded hover:bg-muted"
          data-testid="snapshot-print">
          <Printer className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        </button>
      </div>
      <p className="text-xs" data-testid="snapshot-stats">
        <b>{snap.practice_days}</b> practice day{snap.practice_days === 1 ? "" : "s"} ·{" "}
        <b>{snap.attempts}</b> brave tries · <b>{snap.minutes}</b> min
        {snap.sounds_practiced.length > 0 && <> · sounds: <b>{snap.sounds_practiced.join(", ")}</b></>}
        {snap.badges_earned.length > 0 && <> · <b>{snap.badges_earned.length}</b> badge{snap.badges_earned.length === 1 ? "" : "s"} earned</>}
      </p>
      <p className="text-[10px] text-muted-foreground">
        Tap the printer for a shareable keepsake — effort and participation only, never a score.
      </p>
    </div>
  );
};

// ------------------------------------------------------------ practice trend
export const PracticeTrend = ({ childId }) => {  const [trend, setTrend] = useState([]);

  useEffect(() => {
    if (!childId) return;
    api.get(`/speech/children/${childId}/practice-trend`)
      .then(({ data }) => setTrend((data.trend || []).filter((t) => t.avg_phoneme_score != null)))
      .catch(() => setTrend([]));
  }, [childId]);

  if (trend.length < 2) return null;
  return (
    <div className="space-y-1" data-testid="parent-practice-trend">
      <p className="text-[11px] font-bold">Practice progress
        <span className="text-muted-foreground font-normal"> — steady effort over the weeks (not a test score)</span>
      </p>
      <div className="h-28">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={trend} margin={{ top: 5, right: 8, bottom: 0, left: -24 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.4} />
            <XAxis dataKey="week" tick={{ fontSize: 9 }} tickFormatter={(v) => v.slice(5)} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 9 }} />
            <Tooltip formatter={(v) => [v, "weekly average"]}
              labelFormatter={(v, p) => `Week of ${v} · ${p?.[0]?.payload?.attempts ?? "?"} practices`} />
            <Line type="monotone" dataKey="avg_phoneme_score" stroke="hsl(24 70% 45%)"
              strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// --------------------------------------------------------- beta feedback card
const FEELINGS = [
  ["great", "\u{1F60A} Great"],
  ["okay", "\u{1F642} Okay"],
  ["tricky", "\u{1F615} Tricky"],
];

export const BetaFeedbackCard = ({ beta, onSubmitted }) => {
  const [feeling, setFeeling] = useState("");
  const [note, setNote] = useState("");

  if (!beta?.beta_active || beta.feedback_submitted_this_week) return null;

  const submit = async () => {
    if (!feeling) return toast.error("Pick how practice felt first");
    try {
      const { data } = await api.post("/beta/feedback", { feeling, note });
      toast.success(data.message);
      onSubmitted?.();
    } catch (e) { toast.error(errMessage(e)); }
  };

  return (
    <div className="rounded-lg border border-dashed border-border px-3 py-2.5 space-y-2"
      data-testid="beta-feedback-card">
      <p className="text-[11px] font-bold">How did practice feel this week?</p>
      <div className="flex gap-2 flex-wrap">
        {FEELINGS.map(([key, label]) => (
          <button key={key} type="button" onClick={() => setFeeling(key)}
            className={`rounded-full border px-3 py-1 text-[11px] font-bold transition-colors ${
              feeling === key ? "bg-secondary text-secondary-foreground border-secondary"
                : "border-border hover:bg-muted"}`}
            data-testid={`beta-feedback-${key}`}>
            {label}
          </button>
        ))}
      </div>
      <div className="flex gap-2 items-center">
        <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={300}
          placeholder="Anything you'd like us to know? (optional)"
          className="rounded-md border border-border bg-background px-2 py-1 text-[11px] flex-1"
          data-testid="beta-feedback-note" />
        <button type="button" onClick={submit}
          className="rounded-full bg-secondary text-secondary-foreground px-3 py-1 text-[11px] font-bold"
          data-testid="beta-feedback-submit">
          Send
        </button>
      </div>
    </div>
  );
};
